import { Grid as MuiGrid, type GridProps as MuiGridProps } from '@mui/material';

import type { FlexAlignItems, FlexJustifyContent } from '../../../types/FlexAlignment';
import type { BreakpointValues, Responsive } from '../../../types/Responsive';
import type { NO_SPACING, Spacing } from '../../../types/Spacing';
import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';

type ScalarOf<Value> = Exclude<Value, ReadonlyArray<unknown> | Partial<Record<string, unknown>> | undefined>;

type GridDirection = ScalarOf<MuiGridProps['direction']>;

type GridSize = ScalarOf<MuiGridProps['size']>;

type GridOffset = ScalarOf<MuiGridProps['offset']>;

type GridWrap = NonNullable<MuiGridProps['wrap']>;

type GridSpacing = Spacing | BreakpointValues<Spacing | typeof NO_SPACING>;

type ContainerLayoutProps = {
  direction?: Responsive<GridDirection>;
  spacing?: GridSpacing;
  rowSpacing?: GridSpacing;
  columnSpacing?: GridSpacing;
  alignItems?: Responsive<FlexAlignItems>;
  justifyContent?: Responsive<FlexJustifyContent>;
  wrap?: GridWrap;
  columns?: Responsive<number>;
};

type ItemLayoutProps = {
  size?: Responsive<GridSize>;
  offset?: Responsive<GridOffset>;
};

type ContainerProps = ContainerLayoutProps & {
  container: true;
};

type ItemProps = { [Key in keyof ContainerLayoutProps]?: never } & {
  container?: false;
};

type MuiGridBaseProps = Omit<
  MuiGridProps,
  keyof ContainerLayoutProps | keyof ItemLayoutProps | 'container' | 'unstable_level'
>;

type Props = WithoutStyleOverrides<MuiGridBaseProps> & ItemLayoutProps & (ContainerProps | ItemProps);

export function Grid(props: Props) {
  const { alignItems, justifyContent, ...gridProps } = props;

  return <MuiGrid {...gridProps} sx={{ alignItems, justifyContent }} />;
}

Grid.muiName = 'Grid';

export type { Props as GridProps };
